import { useState, useEffect } from 'react'

import { CheckmarkCircle03Icon } from '@hugeicons/react'
import { toast } from 'sonner'

import { withResourceRecord } from '@/shared/contexts'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useResourceRecordContext } from '@/shared/hooks'
import { useQrCode } from '@/shared/hooks/useQrCode'
import { api } from '@/shared/lib/api'
import { showErrorAlert } from '@/shared/lib/errors'
import { Button, LoadWrapper } from '@/shared/ui'
import { PageHeader, Screen } from '@/shared/ui/Layout'
import { QrCodeReaderBottomSheet } from '@/shared/ui/QrCodeReader/BottomSheet'
import { Resource } from '@/shared/ui/Resource'

import { PartnerCheckInDone } from './components/PartnerCheckInDone'
import { PartnerShowSkeleton } from './components/PartnerShowSkeleton'
import { getConfig, humanName } from './config'
import { PartnerAbout } from './premium/PartnerAbout'
import { PartnerBasicProfile } from './premium/PartnerBasicProfile'
import { PartnerCatalog } from './premium/PartnerCatalog'
import { PartnerContactActions } from './premium/PartnerContactActions'
import { PartnerCover } from './premium/PartnerCover'
import { PartnerIdentityCard } from './premium/PartnerIdentityCard'
import { PartnerLeadForm } from './premium/PartnerLeadForm'
import { PartnerTags } from './premium/PartnerTags'


export const DigitalProductPartnersShow = withResourceRecord(() => {
  const [checkInOpen, setCheckInOpen] = useState(false)
  const [checkInStatus, setCheckInStatus] = useState('')

  const { digitalProductId } = useDigitalProductContext()
  const {
    record: partner,
    loadWrapperConfig,
    refetch
  } = useResourceRecordContext()

  const { getQrCode } = useQrCode()

  const [premium, setPremium] = useState(null)
  const [leadFormOpen, setLeadFormOpen] = useState(false)

  // Premium data (catalog + form presence) comes from the public profile endpoint
  // built for gamification consumption. WhatsApp/CTA already ride on partner.meta.
  // D5: only premium partners expose the premium profile — skip the fetch otherwise.
  useEffect(() => {
    if (!partner?.uid || !partner?.premium) return

    let active = true
    api
      .get(`/public/partner_profiles/${partner.uid}`)
      .then(res => {
        if (active) setPremium(res.data.record)
      })
      .catch(() => {
        // A failed premium fetch must not break the base profile.
        if (active) setPremium(null)
      })

    return () => {
      active = false
    }
  }, [partner?.uid, partner?.premium])

  const resetCheckInSheet = () => {
    setCheckInOpen(false)
    setCheckInStatus('')
  }

  useEffect(() => {
    resetCheckInSheet()
  }, [partner?.id])

  const openCheckInSheet = () => {
    setCheckInStatus('')
    setCheckInOpen(true)
  }

  const handleCheckIn = async qrCode => {
    if (checkInStatus === 'loading') return
    if (partner?.finished) return
    setCheckInStatus('loading')

    try {
      const data = await getQrCode(qrCode)
      const { resource_id, resource_type } = data?.record || {}

      if (resource_type !== 'Partner')
        throw new Error('Este QR Code não é de um parceiro')
      if (resource_id !== partner.id) {
        throw new Error('QR Code não corresponde ao parceiro selecionado')
      }

      await api.post(
        `/user/digital_products/${digitalProductId}/progress/finish_resource`,
        {
          record: {
            resource_id: partner.id,
            resource_type: 'Partner'
          }
        }
      )

      await refetch()
      toast.success('Check-in realizado com sucesso!')
    } catch (error) {
      showErrorAlert(error)
    } finally {
      resetCheckInSheet()
    }
  }

  // Rewards + inline check-in. Kept per product decision even though the handoff
  // omits it (the QR scan also lives in the shell bottom nav). Rendered inside a
  // card on the premium layout; inline on the non-premium base header.
  //
  // Reward display and check-in are independent conditions on purpose: they used
  // to share one `has_rewards` gate, but has_rewards only governs reward delivery
  // since b56fa5400 — check-in availability is `checkin_active`, a separate field.
  // The "Check-in feito" badge depends only on `finished`, never on has_rewards
  // nor checkin_active, so a completed check-in doesn't disappear if availability
  // is turned off afterward.
  const rewardsCheckIn = (partner?.has_rewards || partner?.checkin_active || partner?.finished) ? (
    <div className="flex flex-col gap-3">
      {partner?.has_rewards ? (
        <Resource.Root
          type="Partner"
          id={partner.id}
          pointsReward={partner.point_reward}
          coinsReward={partner.coin_reward}
          finished={partner.finished}
        >
          <Resource.Rewards className="px-2 justify-between" />
        </Resource.Root>
      ) : null}
      {partner?.finished ? (
        <PartnerCheckInDone />
      ) : partner?.checkin_active ? (
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full gap-2"
          onClick={openCheckInSheet}
          leadingIcon={<CheckmarkCircle03Icon size={18} variant="stroke" />}
          showTrailingIcon={false}
        >
          Check-in
        </Button>
      ) : null}
    </div>
  ) : null

  return (
    <Screen className="min-h-0 flex-1 overflow-hidden">
      {!partner?.premium && (
        <Screen.Context>
          <PageHeader
            title={partner?.name ?? humanName.singular}
            backHref="/parceiros"
          />
        </Screen.Context>
      )}
      <Screen.Content className={partner?.premium ? 'pb-6 pt-0' : 'pb-6 pt-1'}>
        <LoadWrapper {...loadWrapperConfig} skeleton={<PartnerShowSkeleton />}>
          {partner?.premium ? (
            /* ── Premium layout (handoff #1a) ────────────────────────────── */
            <div className="pb-8">
              <PartnerCover
                partner={partner}
                coverUrl={premium?.banner?.file?.url}
              />
              <div className="flex flex-col gap-5">
                <PartnerIdentityCard partner={partner} />

              <PartnerContactActions
                partner={partner}
                hasForm={premium?.has_form}
                onOpenForm={() => setLeadFormOpen(true)}
              />

              {rewardsCheckIn && (
                <div className="rounded-2xl bg-white p-4 shadow-[0_2px_10px_rgba(76,29,149,.07)]">
                  {rewardsCheckIn}
                </div>
              )}

              <PartnerAbout about={partner?.meta?.about} />

              <PartnerCatalog items={premium?.catalog || []} partnerId={partner?.uid} />

              <PartnerTags tags={premium?.networking_tags || []} />

              {(premium?.has_form || leadFormOpen) && (
                <PartnerLeadForm
                  partner={partner}
                  digitalProductId={digitalProductId}
                  form={premium?.form}
                  source={leadFormOpen ? 'cta' : 'profile_form'}
                  alreadySubmitted={premium?.lead_submitted}
                />
              )}
              </div>
            </div>
          ) : (
            /* ── Non-premium layout (handoff #1d) ─────────────────────────── */
            <PartnerBasicProfile
              partner={partner}
              rewardsCheckIn={rewardsCheckIn}
            />
          )}
        </LoadWrapper>
        <QrCodeReaderBottomSheet
          title={<>Check-in {partner?.name}</>}
          open={checkInOpen}
          status={checkInStatus}
          onClose={resetCheckInSheet}
          onSuccess={handleCheckIn}
          successMessage="Check-in realizado com sucesso!"
        />
      </Screen.Content>
    </Screen>
  )
}, getConfig)
